import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from 'ionic-ui';
import {
  cartOutline,
  flashOutline,
  homeOutline,
  bagHandleOutline,
  walletOutline,
  // callOutline,
  // megaphoneOutline,
  // giftOutline,
  settingsOutline,
  helpCircleOutline,
  storefrontOutline,
  chatbubbleEllipsesOutline,
} from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { toggleSideBar } from '../../store/slices/sideBar/sideBarSlice';
import useSignOut from '../../services/authentication/hooks/useSignOut';
import images from '../../assets/images';
import { getStoreOrderCounts } from '../../services/inventory/getStoreOrderCounts';

const DashboardSideBar = React.memo(({ match }: { match: string }) => {
  const { isOpen: sideBarIsOpen } = useSelector(
    (state: RootState) => state.sideBar
  );

  const userProfile = useSelector((state: RootState) => state.userProfile);
  const dispatch = useDispatch();

  // Inputs for the order-counts query — we need the store id and an auth token.
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const activeStore = useSelector(
    (state: RootState) => state.userStores.activeStore
  );

  const setSideBarIsActive = React.useCallback((value: boolean) => {
    dispatch(toggleSideBar(value));
  }, []);

  // Drives the orders nav badge. We use `active` — every non-terminal state
  // (successful + marked_as_packed + shipped + delivered) — so the badge
  // reflects everything still in flight, not just orders sitting at
  // 'successful'.
  const { data: orderCounts } = useQuery({
    queryKey: ['store-order-counts', activeStore?.id],
    enabled: Boolean(authToken) && Boolean(activeStore?.id),
    queryFn: () => getStoreOrderCounts(activeStore!.id, authToken as string),
    refetchInterval: 60_000, // refresh every minute so the badge stays live
    staleTime: 30_000,
  });

  const ordersBadge = orderCounts?.active ?? 0;

  const topLinks = useMemo(
    () => [
      {
        id: 'dashboard/get-started',
        isActive: (match || '') === '/dashboard/get-started',
        icon: flashOutline,
        label: 'Get Started',
      },
      {
        id: 'dashboard/',
        isActive: ['/dashboard/', '/dashboard'].includes(match),
        icon: homeOutline,
        label: 'Dashboard',
      },
      {
        id: 'dashboard/orders',
        isActive: (match || '') === '/dashboard/orders',
        icon: bagHandleOutline,
        label: 'Orders',
        // Only show the badge when there are actually orders awaiting pack —
        // 0 collapses to nothing thanks to SidebarLink's truthy check.
        badge: ordersBadge,
      },
      {
        id: 'dashboard/catalog',
        isActive: (match || '') === '/dashboard/catalog',
        icon: cartOutline,
        label: 'Catalog',
      },
      {
        id: 'dashboard/inventory',
        isActive: (match || '') === '/dashboard/inventory',
        icon: storefrontOutline,
        label: 'Inventory',
      },
      {
        id: 'dashboard/earnings',
        isActive: (match || '') === '/dashboard/earnings',
        icon: walletOutline,
        label: 'Earnings',
      },
      {
        id: 'dashboard/chat',
        isActive: match.includes('/dashboard/chat'),
        icon: chatbubbleEllipsesOutline,
        label: 'Chat',
      },
      // {
      //   id: 'dashboard/ads',
      //   isActive: (match || '') === '/dashboard/ads',
      //   icon: megaphoneOutline,
      //   label: 'Ads',
      // },
      // {
      //   id: 'dashboard/airtime-data',
      //   isActive: (match || '') === '/dashboard/airtime-data',
      //   icon: callOutline,
      //   label: 'Buy Airtime / Data',
      // },
      // {
      //   id: 'dashboard/referral-program',
      //   isActive: (match || '') === '/dashboard/referral-program',
      //   icon: giftOutline,
      //   label: 'Referral Program',
      // },
    ],
    [match, ordersBadge]
  );

  const bottomLinks = useMemo(
    () => [
      {
        id: 'dashboard/help&support',
        isActive: match.includes('/dashboard/help&support'),
        icon: helpCircleOutline,
        label: 'Help & Support',
      },
      {
        id: 'dashboard/settings',
        isActive: match.includes('/dashboard/settings'),
        icon: settingsOutline,
        label: 'Settings',
      },
    ],
    [match]
  );

  const profile = {
    image:
      userProfile?.file_data?.profile_image?.file_url || images.placeholder,
    name: userProfile?.user?.first_name || 'Store owner',
    email: userProfile?.user?.email || 'Store email',
  };

  const { requestSignOut } = useSignOut();

  return (
    <div>
      <Sidebar
        sidebarOpen={sideBarIsOpen}
        setSidebarOpen={setSideBarIsActive}
        topOptions={topLinks}
        bottomOptions={bottomLinks}
        logOutFunc={requestSignOut}
        profile={profile}
      />
    </div>
  );
});

export default DashboardSideBar;
DashboardSideBar.displayName = 'DashboardSideBar';
